/*
  One place that knows what a period is. The report, the picker, the csv and the
  pdf all speak the same jjjj-mm-dd strings, so nothing has to guess whether a
  date is local or utc.
*/

const pad = (value) => String(value).padStart(2, "0");

export const toDay = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const parseDay = (day) => {
  const [year, month, date] = String(day).split("-").map(Number);

  return new Date(year, month - 1, date);
};

const SEPTEMBER = 8;

// A club counts in werkjaren, not calendar years: everything from the first of
// September up to the last day of August.
const workYearOf = (date) =>
  date.getMonth() >= SEPTEMBER ? date.getFullYear() : date.getFullYear() - 1;

export const periodPresets = [
  {
    key: "thisMonth",
    label: "Deze maand",
    range: (today) => ({
      start: toDay(new Date(today.getFullYear(), today.getMonth(), 1)),
      end: toDay(today),
    }),
  },
  {
    key: "lastMonth",
    label: "Vorige maand",
    range: (today) => ({
      start: toDay(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      end: toDay(new Date(today.getFullYear(), today.getMonth(), 0)),
    }),
  },
  {
    key: "quarter",
    label: "Dit kwartaal",
    range: (today) => ({
      start: toDay(new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1)),
      end: toDay(today),
    }),
  },
  {
    key: "workYear",
    label: "Dit werkjaar",
    range: (today) => ({
      start: toDay(new Date(workYearOf(today), SEPTEMBER, 1)),
      end: toDay(today),
    }),
  },
  {
    key: "lastWorkYear",
    label: "Vorig werkjaar",
    range: (today) => ({
      start: toDay(new Date(workYearOf(today) - 1, SEPTEMBER, 1)),
      end: toDay(new Date(workYearOf(today), SEPTEMBER, 0)),
    }),
  },
];

export const presetRange = (key, today = new Date()) =>
  periodPresets.find((preset) => preset.key === key)?.range(today) ?? null;

export const defaultPeriod = () => presetRange("workYear");

/**
 * Which preset a period corresponds to, so the picker can light up the button
 * the numbers on screen actually belong to. Null for a hand-picked range.
 */
export const matchingPreset = ({ start, end }, today = new Date()) =>
  periodPresets.find((preset) => {
    const range = preset.range(today);

    return range.start === start && range.end === end;
  })?.key ?? null;

const months = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];

export const formatDay = (day) => {
  if (!day) return "";
  const [year, month, date] = String(day).split("-");

  return `${Number(date)}/${month}/${year}`;
};

export const formatDayLong = (day) => {
  if (!day) return "";
  const date = parseDay(day);

  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

export const formatPeriod = ({ start, end }) =>
  `${formatDayLong(start)} t.e.m. ${formatDayLong(end)}`;

const decimal = (value, digits = 2, grouped = true) => {
  const text = Number(value ?? 0).toFixed(digits).replace(".", ",");

  return grouped ? text.replace(/\B(?=(\d{3})+(?!\d)(?=,))/g, ".") : text;
};

export const formatMoney = (value) => {
  const amount = Number(value ?? 0);

  return `${amount < 0 ? "-" : ""}€ ${decimal(Math.abs(amount))}`;
};

export const formatNumber = (value) =>
  new Intl.NumberFormat("nl-BE").format(Number(value ?? 0));

// The csv is opened in Excel with Belgian regional settings, which reads a comma
// as the decimal separator. Thousands are left ungrouped: a point in the middle
// of a number is one regional setting away from being read as a decimal point.
export const csvNumber = (value) => decimal(value, 2, false);
